import { COLORS } from '@/styles/color';
import { paddingX, paddingY } from '@/styles/layout/utils';
import { customTextSize, remCalc } from '@/styles/typography/utils';
import { debounce } from 'lodash';
import { FC, UIEventHandler, useLayoutEffect, useRef, useState } from 'react';
import styled from 'styled-components';

export const ItemsWrapper = styled.div`
  display: flex;
  flex-direction: row;
  column-gap: ${remCalc(16)};

  overflow: auto;
  -webkit-overflow-scrolling: touch;
  scroll-snap-type: x mandatory;

  ${paddingX(16)}

  &::after {
    content: '';
    padding-right: 1px;
  }

  & > * {
    scroll-snap-align: center;
    scroll-snap-stop: always;

    flex-grow: 1;
    flex-shrink: 0;
    flex-basis: 75%;
  }

  ::-webkit-scrollbar {
    display: none;
  }
`;

const Navigation = styled.nav`
  display: flex;
  flex-direction: row;
  align-items: center;
  justify-content: center;
  ${paddingY(16)}
`;

interface ItemButtonProps {
  isActive: boolean;
}

const ItemButton = styled.button<ItemButtonProps>`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${remCalc(24)};
  height: ${remCalc(24)};
  ${customTextSize(0, 0)}

  &::before {
    content: '';
    width: ${remCalc(8)};
    height: ${remCalc(8)};
    background-color: ${COLORS.BLACK};
    opacity: ${(props) => (props.isActive ? 1 : 0.5)};
    border-radius: 100%;
  }
`;

interface CarouselProps extends PropsWithClassName {
  items: React.ReactNode[];
}

const Carousel: FC<CarouselProps> = (props) => {
  const ref = useRef<HTMLDivElement>(null);
  const [currentPosition, setCurrentPosition] = useState<number>(0);

  useLayoutEffect(() => {
    const handler = debounce(() => {
      if (!ref.current || ref.current.childNodes.length === 0) return;

      const margin = (ref.current?.clientWidth - (ref.current.firstChild as HTMLElement).clientWidth) / 2;

      (ref.current.firstChild as HTMLElement).style.marginLeft = remCalc(margin);
      (ref.current.lastChild as HTMLElement).style.marginRight = remCalc(margin - 1);
    }, 150);

    window.addEventListener('resize', handler);

    handler();

    return () => {
      window.removeEventListener('resize', handler);
    };
  }, [ref]);

  const onScroll: UIEventHandler<HTMLDivElement> = (e) => {
    const el = e.target as HTMLDivElement;

    if (el.scrollLeft === 0) {
      setCurrentPosition(0);
    } else if (el.scrollLeft === el.scrollWidth - el.clientWidth) {
      setCurrentPosition(props.items.length - 1);
    } else {
      /* prettier-ignore */
      const positions = Array.from(el.childNodes).map((node: HTMLDivElement) => node.offsetLeft + (node.clientWidth / 2));

      /* prettier-ignore */
      const scrollCenter = el.scrollLeft + (el.clientWidth / 2);
      const closest = positions.reduce((prev, curr) =>
        Math.abs(curr - scrollCenter) < Math.abs(prev - scrollCenter) ? curr : prev,
      );

      setCurrentPosition(positions.indexOf(closest));
    }
  };

  const changeSlide = (index: number) => {
    const el = ref.current as HTMLDivElement;
    const node = el?.childNodes?.[index] as HTMLDivElement;

    /* prettier-ignore */
    const offsetCenter = node?.offsetLeft + (node?.clientWidth / 2);

    /* prettier-ignore */
    const scrollCenter = offsetCenter - (el.clientWidth / 2);

    ref.current?.scrollTo({
      left: scrollCenter,
      behavior: 'smooth',
    });
  };

  return (
    <div className={props.className}>
      <ItemsWrapper ref={ref} onScroll={debounce(onScroll, 150)}>
        {props.items.map((item) => item)}
      </ItemsWrapper>

      <Navigation>
        {props.items.map((item, index) => (
          <ItemButton
            key={`carousel-dot-${index}`}
            type="button"
            isActive={currentPosition === index}
            onClick={() => changeSlide(index)}
          >
            {index + 1}
          </ItemButton>
        ))}
      </Navigation>
    </div>
  );
};

export default Carousel;
